/**
 * Window and category keys. They are one namespace -- no two windows, no two categories, and
 * no window and category may share a key -- because a door's wording is addressed by key
 * alone, whatever kind of thing the key names.
 */

/** A key as it is typed: only the characters a key can hold, and nothing trimmed off the
 * ends, so a hyphen can be typed before the word that follows it. No prefix is added. */
export const keyAsTyped = (value: string): string =>
  value.toLowerCase().replace(/[^a-z0-9-_]/g, "-");

/** A starting key from a name, until someone types their own. */
export const keyFromName = (name: string): string =>
  keyAsTyped(name.trim())
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

/** `key`, or `key-2`, `key-3` ... -- the first one nothing else holds. */
export const uniqueKey = (key: string, taken: readonly string[]): string => {
  if (!key) return "";
  let candidate = key;
  let suffix = 2;
  while (taken.includes(candidate)) {
    candidate = `${key}-${suffix}`;
    suffix += 1;
  }
  return candidate;
};

export type KeyProblem = "" | "empty" | "taken";

/** What is wrong with each window's and each category's key, checked across both lists. */
export const keyProblems = (
  windowKeys: readonly string[],
  categoryKeys: readonly string[],
): { windows: KeyProblem[]; categories: KeyProblem[] } => {
  const counts = new Map<string, number>();
  [...windowKeys, ...categoryKeys].forEach((key) => counts.set(key, (counts.get(key) ?? 0) + 1));
  const problem = (key: string): KeyProblem =>
    !key.trim() ? "empty" : (counts.get(key) ?? 0) > 1 ? "taken" : "";
  return { windows: windowKeys.map(problem), categories: categoryKeys.map(problem) };
};

export const hasKeyProblem = (problems: ReturnType<typeof keyProblems>): boolean =>
  [...problems.windows, ...problems.categories].some(Boolean);
